import React from "react";
import { Field, FieldType, FieldGroup } from "../../entities/Metadata";
import { ApprovalDecision, ApprovalRequest, ApprovalRequestStatus } from "../../store/ApprovalCenterStore";
import { FormatDate } from "../utils/common";
import { GridCellShowMoreComponent } from "../common/ShowMoreComponent/ShowMoreComponent";

export const DecisionDateField: Field = {
    id: "decisionDate",
    name: "DecisionDate",
    type: FieldType.Date,
    isNative: false,
    isSystem: false,
    isCustom: false,
    label: "Decision Date",
    isReadonly: true,
    isFake: true,
    group: FieldGroup.SystemFields,
    settings: {
        views: {
            list: {
                minWidth: 100,
                maxWidth: 115
            }
        }
    }
};

export const DecisionMakersField: Field = {
    id: "decisionMakers",
    name: "DecisionMakers",
    type: FieldType.User,
    isNative: false,
    isSystem: false,
    isCustom: false,
    label: "Approved/Rejected By",
    isReadonly: true,
    isFake: true,
    group: FieldGroup.SystemFields,
    settings: {
        multichoice: true,
        views: {
            list: {
                minWidth: 140,
                maxWidth: 180
            }
        }
    }
};

export const ApproverField: Field = {
    id: "approver",
    name: "Approver",
    type: FieldType.User,
    isNative: false,
    isSystem: false,
    isCustom: false,
    label: "Approver",
    isReadonly: true,
    isFake: true,
    group: FieldGroup.SystemFields,
    settings: {
        multichoice: true,
        views: {
            list: {
                minWidth: 140,
                maxWidth: 180
            }
        }
    }
};

export const ApproverCommentField: Field = {
    id: "approverComment",
    name: "ApproverComment",
    type: FieldType.Text,
    isNative: false,
    isSystem: false,
    isCustom: false,
    label: "Approver Comment",
    isReadonly: true,
    isFake: true,
    group: FieldGroup.SystemFields,
    settings: {
        views: {
            list: {
                minWidth: 150,
                maxWidth: 300
            }
        }
    }
};

const formatApproverComments = (decisions: ApprovalDecision[]): string =>
    decisions
        .filter(d => !!d.comment)
        .map(d => `${d.approver.fullName}: ${d.comment}`)
        .join("\n");

const ApproverComments = ({ decisions }: { decisions: ApprovalDecision[] }) => {
    const decisionsWithComments = decisions.filter(d => !!d.comment);
    if (!decisionsWithComments.length) {
        return <span />;
    }

    const titleText = formatApproverComments(decisions);

    return (
        <GridCellShowMoreComponent>
            <div title={titleText} className="multiline">
                {decisionsWithComments.map((d, i) => (
                    <div key={i}>
                        <strong>{d.approver.fullName}:</strong>
                        {" "}{d.comment}
                    </div>
                ))}
            </div>
        </GridCellShowMoreComponent>
    );
};

const resolveDecisionDate = (request: ApprovalRequest, currentUserId: string): Date | undefined => {
    const userDecision = request.decisions.find(d => d.approver.id === currentUserId);
    if (userDecision) {
        return userDecision.decisionDate;
    }
    if (request.attributes.Status === ApprovalRequestStatus.Pending) {
        return undefined;
    }
    return request.decisions[request.decisions.length - 1]?.decisionDate;
};

export const commonFakeFieldValueGetters: Partial<Record<string, (request: ApprovalRequest, currentUserId: string) => unknown>> = {
    [DecisionDateField.name]: (request, currentUserId) => resolveDecisionDate(request, currentUserId) ?? null,
    [ApproverCommentField.name]: request => formatApproverComments(request.decisions),
};

export const commonFakeFieldRenderers: Partial<Record<string, (request: ApprovalRequest, currentUserId: string) => JSX.Element>> = {
    [DecisionDateField.name]: (request, currentUserId) =>
        <span>{FormatDate(resolveDecisionDate(request, currentUserId))}</span>,
    [ApproverCommentField.name]: request => <ApproverComments decisions={request.decisions} />,
};
